import { FC, memo } from 'react';
import { LazyPdfRenderer } from './LazyPdfRenderer';
import { Page as PageData } from '@/api';
import { calculateA4HeightAndAspectRatio } from '@/utils';

interface PageProps {
  page: PageData;
  width: number;
  scale: number;
  className?: string;
}

const PageView: FC<PageProps> = memo(({ page, width, scale, className = '' }) => {
  // Size the PDF container to the page's true aspect ratio so non-A4 / landscape
  // pages aren't clipped or letter-boxed. Falls back to A4 if pageDimensions is
  // missing/zero (handled inside calculateA4HeightAndAspectRatio).
  const pageHeight =
    calculateA4HeightAndAspectRatio({
      width: page.pageDimensions?.width,
      height: page.pageDimensions?.height,
      newWidth: width,
    }) * scale;

  // Get the label and styling based on page properties
  const getPageLabelInfo = () => {
    //User Marked Breach
    if (page.confirmedBreach)
      return {
        text: 'Breach',
        bgColor: '#DD4F45',
        textColor: '#FFFFFF',
      };
    //User Marked Blank
    if (page.confirmedBlank)
      return {
        text: 'Blank',
        bgColor: '#13898D',
        textColor: '#FFFFFF',
      };
    //AI Detected Breach
    if (page.detectedBreach)
      return {
        text: 'Potential Breach',
        bgColor: '#FFFFFF',
        textColor: '#DD4F45',
      };
    //AI Detected Blank
    if (page.detectedBlank)
      return {
        text: 'Detected Blank',
        bgColor: '#FFFFFF',
        textColor: '#13898D',
      };
    return null;
  };

  const pageLabelInfo = getPageLabelInfo();

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {/* PDF container — sized to the page's true aspect ratio. No `overflow: hidden`
          because if BE-supplied dimensions disagree slightly with react-pdf's intrinsic
          page size, we want the rendering to flow naturally rather than getting clipped. */}
      <div
        className="relative"
        style={{
          width: `${width}px`,
          height: `${pageHeight}px`,
          border: '1px solid #D1D5DB',
        }}
      >
        <LazyPdfRenderer pdfFile={page.pageUri} width={width} height={pageHeight} />
      </div>

      {/* Page number (right of centre) and label (right) — width pinned to `width` so
          the label's `right: 0` lines up with the PDF's right edge. The label tab still
          attaches to the bottom of the PDF (top:0) but its text gets vertical padding
          inside the tab so it isn't squished against the page edge. The page number
          drops further below the PDF via mt-2. */}
      <div className="relative flex items-start justify-center" style={{ width: `${width}px` }}>
        {/* Page number — sits 8px below the PDF for visual breathing room. */}
        <div className="mt-2 text-center text-xs text-gray-700">{page.originalPageNumber}</div>

        {/* Page label — hangs down from the bottom of the PDF as a tab. */}
        {pageLabelInfo && (
          <div
            className="absolute top-0 right-0 px-2 py-1 text-xs shadow-sm"
            style={{
              backgroundColor: pageLabelInfo.bgColor,
              color: pageLabelInfo.textColor,
              borderBottomLeftRadius: '4px',
              borderBottomRightRadius: '4px',
              borderTopLeftRadius: '0px',
              borderTopRightRadius: '0px',
            }}
          >
            {pageLabelInfo.text}
          </div>
        )}
      </div>
    </div>
  );
});

export { PageView };
